import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { LanguageProvider } from "./i18n/LanguageContext";
import ProtectedRoute from "./auth/ProtectedRoute";
import LoginPage from "./pages/LoginPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import DashboardPage from "./pages/DashboardPage";
import PositionsPage from "./pages/PositionsPage";
import StatementsPage from "./pages/StatementsPage";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminClientsPage from "./pages/admin/AdminClientsPage";
import AdminInvitesPage from "./pages/admin/AdminInvitesPage";
import AdminDepositsPage from "./pages/admin/AdminDepositsPage";
import AdminStatementsPage from "./pages/admin/AdminStatementsPage";
import ExploreHubPage from "./pages/explore/ExploreHubPage";
import ExplorePhilosophyPage from "./pages/explore/ExplorePhilosophyPage";
import ExploreCompliancePage from "./pages/explore/ExploreCompliancePage";
import ExploreFaqPage from "./pages/explore/ExploreFaqPage";
import KycPage from "./pages/KycPage";
import EventsPage from "./pages/EventsPage";
import PlansPage from "./pages/PlansPage";
import AdminKycPage from "./pages/admin/AdminKycPage";
import AdminEventsPage from "./pages/admin/AdminEventsPage";
import AdminPlansPage from "./pages/admin/AdminPlansPage";
import AdminInvestmentRequestsPage from "./pages/admin/AdminInvestmentRequestsPage";
import AdminOnboardingFunnelPage from "./pages/admin/AdminOnboardingFunnelPage";
import AdminKycRemindersPage from "./pages/admin/AdminKycRemindersPage";
import UnsubscribeRemindersPage from "./pages/UnsubscribeRemindersPage";
import AdminTeamPage from "./pages/admin/AdminTeamPage";
import AdminSecurityPage from "./pages/admin/AdminSecurityPage";
import AdminPasswordAuditPage from "./pages/admin/AdminPasswordAuditPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import PublicReferralPage from "./pages/PublicReferralPage";
import IntroducerDashboardPage from "./pages/introducer/IntroducerDashboardPage";
import IntroducerReferralsPage from "./pages/introducer/IntroducerReferralsPage";
import IntroducerContractPage from "./pages/introducer/IntroducerContractPage";
import IntroducerStatementsPage from "./pages/introducer/IntroducerStatementsPage";
import IntroducerGuidePage from "./pages/introducer/IntroducerGuidePage";
import AdminIntroducersPage from "./pages/admin/AdminIntroducersPage";
import AdminReferralLeadsPage from "./pages/admin/AdminReferralLeadsPage";
import { homePathForRole } from "./lib/roleRoutes";

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center br-aurora">
        <div className="font-mono-fin text-xs tracking-[0.32em] uppercase text-[color:var(--br-text-dim)]">
          Verifica sessione…
        </div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return <Navigate to={homePathForRole(user.role)} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <LanguageProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/kyc-reminders/unsubscribe/:token" element={<UnsubscribeRemindersPage />} />
            <Route path="/referral/:code" element={<PublicReferralPage />} />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />

            {/* Client routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute role="client">
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/positions"
              element={
                <ProtectedRoute role="client">
                  <PositionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/statements"
              element={
                <ProtectedRoute role="client">
                  <StatementsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/explore"
              element={
                <ProtectedRoute role={["client", "introducer"]}>
                  <ExploreHubPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/explore/philosophy"
              element={
                <ProtectedRoute role={["client", "introducer"]}>
                  <ExplorePhilosophyPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/explore/compliance"
              element={
                <ProtectedRoute role={["client", "introducer"]}>
                  <ExploreCompliancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/explore/faq"
              element={
                <ProtectedRoute role={["client", "introducer"]}>
                  <ExploreFaqPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kyc"
              element={
                <ProtectedRoute role="client">
                  <KycPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/events"
              element={
                <ProtectedRoute role={["client", "introducer"]}>
                  <EventsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plans"
              element={
                <ProtectedRoute role={["client", "introducer"]}>
                  <PlansPage />
                </ProtectedRoute>
              }
            />

            {/* Admin routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute role="admin">
                  <AdminDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/clients"
              element={
                <ProtectedRoute role="admin">
                  <AdminClientsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/invites"
              element={
                <ProtectedRoute role="admin">
                  <AdminInvitesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/deposits"
              element={
                <ProtectedRoute role="admin">
                  <AdminDepositsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/statements"
              element={
                <ProtectedRoute role="admin">
                  <AdminStatementsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/kyc"
              element={
                <ProtectedRoute role="admin">
                  <AdminKycPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/events"
              element={
                <ProtectedRoute role="admin">
                  <AdminEventsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/plans"
              element={
                <ProtectedRoute role="admin">
                  <AdminPlansPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/investment-requests"
              element={
                <ProtectedRoute role="admin">
                  <AdminInvestmentRequestsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/funnel"
              element={
                <ProtectedRoute role="admin">
                  <AdminOnboardingFunnelPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/kyc-reminders"
              element={
                <ProtectedRoute role="admin">
                  <AdminKycRemindersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/team"
              element={
                <ProtectedRoute role="admin">
                  <AdminTeamPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/security"
              element={
                <ProtectedRoute role="admin">
                  <AdminSecurityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/password-audit"
              element={
                <ProtectedRoute role="admin">
                  <AdminPasswordAuditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/introducers"
              element={
                <ProtectedRoute role="admin">
                  <AdminIntroducersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/referral-leads"
              element={
                <ProtectedRoute role="admin">
                  <AdminReferralLeadsPage />
                </ProtectedRoute>
              }
            />

            {/* Introducer portal */}
            <Route
              path="/introducer"
              element={
                <ProtectedRoute role="introducer">
                  <IntroducerDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/introducer/referrals"
              element={
                <ProtectedRoute role="introducer">
                  <IntroducerReferralsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/introducer/contract"
              element={
                <ProtectedRoute role="introducer">
                  <IntroducerContractPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/introducer/statements"
              element={
                <ProtectedRoute role="introducer">
                  <IntroducerStatementsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/introducer/guide"
              element={
                <ProtectedRoute role="introducer">
                  <IntroducerGuidePage />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </LanguageProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
